import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { aiService, type AIEmailRequest } from '../../services/aiService';
import type { EmailBlockData } from './ModernEmailBuilder';
import { convertAIEmailToBlocks } from './utils/aiEmailConverter';

interface AIEmailGeneratorProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (blocks: EmailBlockData[], subject: string, previewText: string) => void;
  campaignName?: string;
  senderName?: string;
}

const EMAIL_TYPE_OPTIONS = [
  { value: 'welcome', label: 'Welcome Email' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'promotional', label: 'Promotional' },
  { value: 'transactional', label: 'Transactional' },
  { value: 'announcement', label: 'Announcement' },
  { value: 'custom', label: 'Custom' },
] as const;

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'promotional', label: 'Promotional' },
] as const;

const PROMPT_SUGGESTIONS = [
  'Create a welcome email for new subscribers',
  'Write a newsletter about our latest product updates',
  'Generate a promotional email for a 50% off sale',
  'Create a transactional email for order confirmation',
  'Write an announcement email for a new feature launch',
  'Create a re-engagement email for inactive subscribers',
];

export const AIEmailGenerator: React.FC<AIEmailGeneratorProps> = ({
  isOpen,
  onClose,
  onGenerate,
  campaignName,
  senderName,
}) => {
  const [prompt, setPrompt] = useState('');
  const [emailType, setEmailType] = useState<AIEmailRequest['emailType']>('custom');
  const [tone, setTone] = useState<AIEmailRequest['tone']>('professional');
  const [includeCallToAction, setIncludeCallToAction] = useState(true);
  const [includeImages, setIncludeImages] = useState(false);
  const [additionalContext, setAdditionalContext] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConfigured, setIsConfigured] = useState(false);

  useEffect(() => {
    setIsConfigured(aiService.isConfigured());
  }, []);

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setPrompt('');
      setEmailType('custom');
      setTone('professional');
      setIncludeCallToAction(true);
      setIncludeImages(false);
      setAdditionalContext('');
      setError(null);
    }
  }, [isOpen]);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt to generate the email');
      return;
    }

    if (!isConfigured) {
      setError('AI service is not configured. Please add your Google Gemini API key to environment variables.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const request: AIEmailRequest = {
        prompt: prompt.trim(),
        emailType,
        tone,
        includeCallToAction,
        includeImages,
        campaignName,
        senderName,
        additionalContext: additionalContext.trim() || undefined,
      };

      const response = await aiService.generateEmailFromPrompt(request);

      if (response.success) {
        const blocks = convertAIEmailToBlocks(response);
        onGenerate(blocks, response.subject, response.previewText);
        onClose();
      } else {
        setError(response.error || 'Failed to generate email. Please try again.');
      }
    } catch (err) {
      console.error('Error generating email:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while generating the email');
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setPrompt(suggestion);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">AI Email Generator</h2>
              <p className="text-sm text-gray-500">Generate professional emails with AI</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={loading}
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {!isConfigured && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                ⚠️ AI service is not configured. Please add <code className="bg-yellow-100 px-1 rounded">REACT_APP_GEMINI_API_KEY</code> to your environment variables.
              </p>
            </div>
          )}

          {/* Prompt Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What kind of email do you want to create?
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="E.g., Create a welcome email for new customers introducing our product..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              rows={4}
              disabled={loading || !isConfigured}
            />
            
            {/* Prompt Suggestions */}
            <div className="mt-3">
              <p className="text-xs text-gray-500 mb-2">Quick suggestions:</p>
              <div className="flex flex-wrap gap-2">
                {PROMPT_SUGGESTIONS.map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSuggestionClick(suggestion)}
                    disabled={loading || !isConfigured}
                    className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Email Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Type
            </label>
            <select
              value={emailType}
              onChange={(e) => setEmailType(e.target.value as AIEmailRequest['emailType'])}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              disabled={loading || !isConfigured}
            >
              {EMAIL_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Tone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tone
            </label>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value as AIEmailRequest['tone'])}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              disabled={loading || !isConfigured}
            >
              {TONE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Options */}
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={includeCallToAction}
                onChange={(e) => setIncludeCallToAction(e.target.checked)}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                disabled={loading || !isConfigured}
              />
              <span className="text-sm text-gray-700">Include Call-to-Action Button</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={includeImages}
                onChange={(e) => setIncludeImages(e.target.checked)}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                disabled={loading || !isConfigured}
              />
              <span className="text-sm text-gray-700">Include Image Placeholders</span>
            </label>
          </div>

          {/* Additional Context */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Context (Optional)
            </label>
            <textarea
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              placeholder="E.g., Target audience: small business owners, Key message: emphasize affordability..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              rows={2}
              disabled={loading || !isConfigured}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className={`p-4 rounded-lg border ${
              error.includes('Quota Exceeded') || error.includes('quota')
                ? 'bg-yellow-50 border-yellow-200'
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  {error.includes('Quota Exceeded') || error.includes('quota') ? (
                    <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${
                    error.includes('Quota Exceeded') || error.includes('quota')
                      ? 'text-yellow-800'
                      : 'text-red-800'
                  }`}>
                    {error}
                  </p>
                  {error.includes('Quota Exceeded') && (
                    <div className="mt-3 space-y-2">
                      <a
                        href="https://ai.google.dev/pricing"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-yellow-700 hover:text-yellow-900 underline"
                      >
                        View Pricing & Upgrade Plan
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                      <p className="text-xs text-yellow-700 mt-2">
                        💡 Tip: Free tier quotas reset periodically. You can also try again later or upgrade for higher limits.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={loading || !prompt.trim() || !isConfigured}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Generating...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                Generate Email
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

